import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import logger from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingForgotModal = ({ open, onOpenChange, onLoginClick }) => {
  const { t, i18n } = useTranslation();

  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) { setForgotSent(false); setForgotEmail(''); setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, lang: i18n.language || 'en' });
      // Backend always returns 200 for anti-enumeration. Treat 2xx as "request accepted"
      // — does NOT confirm the account exists or that the email was actually dispatched.
      setForgotSent(true);
    } catch (err) {
      // Only surface true transport-level failures (network down, CORS, 5xx).
      // Anti-enumeration: 4xx (validation, rate-limit) is still treated as "sent"
      // to avoid leaking whether the email exists.
      //
      // Exception: 429 is surfaced via a toast so users who hit the rate limit
      // know to wait rather than assume the email silently never arrived. We
      // still show the "sent" state — telling the user they're being rate-limited
      // does not reveal whether their account exists (the limit is per-IP).
      const status = err?.response?.status;
      const isTransportFailure = !status || status >= 500;
      if (isTransportFailure) {
        logger.error('Forgot-password transport failure', { status, message: err?.message });
        toast.error(t('auth.forgotPassword.errorGeneric'));
      } else {
        if (status === 429) {
          toast.error(t('auth.forgotPassword.errorRateLimited'));
        }
        setForgotSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {t('auth.forgotPassword.title')}
          </DialogTitle>
        </DialogHeader>

        {forgotSent ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-1">
              {t('auth.forgotPassword.emailSent')}
            </p>
            <p className="text-gray-500 text-sm mb-4">
              {t('auth.forgotPassword.emailSentDescription')}
            </p>
            <button
              onClick={() => { handleClose(false); onLoginClick(); }}
              className="text-primary hover:underline text-sm font-medium"
            >
              {t('auth.forgotPassword.backToLogin')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-gray-500 text-sm">
              {t('auth.forgotPassword.description')}
            </p>
            <div>
              <Label htmlFor="forgot-email">{t('landing.loginModal.email')}</Label>
              <Input
                id="forgot-email" type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required placeholder="you@company.com"
                data-testid="forgot-email-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
              {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetLink')}
            </Button>
            <button
              type="button"
              onClick={() => { handleClose(false); onLoginClick(); }}
              className="w-full text-center text-sm text-primary hover:underline"
            >
              {t('auth.forgotPassword.backToLogin')}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LandingForgotModal;
